import { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { Client } from 'paho-mqtt'
import { MQTT_API } from '../utils/api'
import { useCurrentUser } from '../hooks/contexts/currentUserContext'
import { Instrument, InstrumentType, TouchMode, SONG_TYPES } from '../utils/types'
import { useConnectedInstruments, useUpdateConnectedInstrument } from '../hooks/contexts/connectedInstrumentsContext'

interface MqttHandlerContextProps {
    mqttIsConnected: boolean
    postChangeSong: ({ message, deviceId, songType }: { message: string; deviceId?: string; songType?: SONG_TYPES }) => void
    postChangeVolume: (message: string, instrumentId: string) => void
    postChangeMasterVolume: (message: string) => void
    postResetNetwork: (instrumentId: string) => void
    postSetInstrumentMode: ({ deviceId, touchMode }: { deviceId?: string; touchMode: TouchMode }) => void
}

export interface ConnectedInstrumentInfo {
    type: InstrumentType
    serialNr: string
    lastOnlineTimestamp?: number
}

const MqttHandlerContext = createContext<MqttHandlerContextProps>({
    mqttIsConnected: false,
    postChangeSong: () => {},
    postChangeVolume: () => {},
    postChangeMasterVolume: () => {},
    postResetNetwork: () => {},
    postSetInstrumentMode: () => {}
})

const generateRandomClientId = () => {
    const randomNumber = Math.floor(Math.random() * 90000) + 10000
    return randomNumber.toString()
}

export const MqttHandlerProvider = ({ children }: { children: ReactNode }) => {
    const currentUser = useCurrentUser()
    const [mqttClient, setMqttClient] = useState<Client | undefined>(undefined)
    const [mqttIsConnected, setMqttIsConnected] = useState<boolean>(false)

    const updateConnectedInstruments = useUpdateConnectedInstrument()
    const connectedInstruments = useConnectedInstruments()

    const customerInstruments = currentUser?.customer?.instruments

    const options = {
        host: 'mqtt-proxy.stordatornfunki.workers.dev',
        port: 443,
        protocol: 'wss',
        clientId: 'web' + generateRandomClientId(),
        userName: MQTT_API.CREDITS.USERNAME,
        password: MQTT_API.CREDITS.PASSWORD,

        //////////////////////////////////////////////////////
        // CHANGED FROM 15 -> 5
        //////////////////////////////////////////////////////

        keepAliveInterval: 15,

        cleanSession: true
    }

    ////// USE EFFECTS //////

    useEffect(() => {
        if (!currentUser?.customer) {
            console.log('No customer connected, skipping MQTT handler setup')
            return
        } else {
            console.log('Customer found, initializing MQTT handler')

            // Create MQTT client
            const client = new Client(options.host, options.port, options.clientId)

            // Configure MQTT client
            configureMqttClient(client)

            // Cleanup when component unmounts
            return () => {
                if (client.isConnected()) {
                    // Prevent reconnect during unmount
                    client.onConnectionLost = () => {
                        setMqttIsConnected(false)
                    }

                    client.disconnect()

                    console.log('Disconnected MQTT client on component unmount')
                }
            }
        }
    }, [currentUser])

    useEffect(() => {
        if (mqttClient) {
            setMqttIsConnected(mqttClient.isConnected())

            if (currentUser?.customer?.id) {
                currentUser?.customer?.instruments?.forEach((instrument) => {
                    mqttClient.subscribe(`/${instrument.deviceId}/status`)
                })
            }
        } else {
            setMqttIsConnected(false)
        }
    }, [mqttClient])

    //////////////////////////////////////////////////////
    // VISIBILITY CHANGE RECONNECT
    //////////////////////////////////////////////////////

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('App became visible')

                if (mqttClient && !mqttClient.isConnected()) {
                    console.log('Reconnecting MQTT after wakeup')

                    //////////////////////////////////////////////////////
                    // ADDED 1 SECOND DELAY
                    //////////////////////////////////////////////////////

                    setTimeout(() => {
                        connectMqttClient(mqttClient)
                    }, 1000)
                }
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [mqttClient])

    //////////////////////////////////////////////////////
    // ONLINE / OFFLINE EVENTS
    //////////////////////////////////////////////////////

    useEffect(() => {
        const handleOnline = () => {
            console.log('Network restored')

            if (mqttClient && !mqttClient.isConnected()) {
                //////////////////////////////////////////////////////
                // ADDED 1 SECOND DELAY
                //////////////////////////////////////////////////////

                setTimeout(() => {
                    connectMqttClient(mqttClient)
                }, 1000)
            }
        }

        const handleOffline = () => {
            console.log('Network lost')

            setMqttIsConnected(false)
        }

        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        return () => {
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('offline', handleOffline)
        }
    }, [mqttClient])

    ////// PRIVATE METHODS //////

    const configureMqttClient = (client: Client) => {
        //////////////////////////////////////////////////////
        // CONNECTION LOST
        //////////////////////////////////////////////////////

        client.onConnectionLost = (error) => {
            console.error(`MQTT connection lost: ${error.errorMessage}`)

            setMqttIsConnected(false)

            //////////////////////////////////////////////////////
            // ADDED 1 SECOND DELAY
            //////////////////////////////////////////////////////

            setTimeout(() => {
                connectMqttClient(client)
            }, 1000)
        }

        //////////////////////////////////////////////////////
        // CONNECT
        //////////////////////////////////////////////////////

        connectMqttClient(client)

        //////////////////////////////////////////////////////
        // MESSAGE ARRIVED
        //////////////////////////////////////////////////////

        client.onMessageArrived = (message: any) => {
            currentUser?.customer?.instruments.forEach((instrument: Instrument) => {
                const deviceIdStatus = `/${instrument.deviceId}/status`

                //////////////////////////////////////////////////////
                // ONLINE
                //////////////////////////////////////////////////////

                if (message.destinationName.includes(deviceIdStatus) && message.payloadString === 'ONLINE') {
                    const existingInstrument = connectedInstruments.find((inst) => inst.deviceId === instrument.deviceId)

                    if (existingInstrument) {
                        updateConnectedInstruments({
                            deviceId: existingInstrument.deviceId,
                            serialNr: existingInstrument.serialNr,
                            type: existingInstrument.type,
                            color: existingInstrument.color,
                            touchMode: existingInstrument.touchMode,
                            currentSong: {
                                ...existingInstrument.currentSong
                            }
                        })
                    } else {
                        updateConnectedInstruments({
                            deviceId: instrument.deviceId,
                            serialNr: instrument.serialNr,
                            type: instrument.type,
                            color: instrument.color,
                            touchMode: instrument.touchMode,
                            currentSong: {
                                id: undefined,
                                songType: undefined,
                                sound: undefined,
                                category: undefined,
                                key: undefined
                            }
                        })
                    }
                }

                //////////////////////////////////////////////////////
                // OFFLINE
                //////////////////////////////////////////////////////

                if (message.destinationName.includes(deviceIdStatus) && message.payloadString === 'OFFLINE') {
                    updateConnectedInstruments(
                        {
                            deviceId: instrument.deviceId,
                            serialNr: instrument.serialNr,
                            type: instrument.type,
                            color: instrument.color,
                            touchMode: instrument.touchMode,
                            currentSong: {
                                id: undefined,
                                songType: undefined,
                                sound: undefined,
                                category: undefined,
                                key: undefined
                            }
                        },
                        true
                    )
                }
            })
        }
    }

    //////////////////////////////////////////////////////
    // CONNECT MQTT CLIENT
    //////////////////////////////////////////////////////

    const connectMqttClient = (client: Client) => {
        //////////////////////////////////////////////////////
        // PREVENT DOUBLE CONNECTS
        //////////////////////////////////////////////////////

        if (client.isConnected()) {
            return
        }

        console.log('Connecting MQTT client...')

        client.connect({
            useSSL: true,

            //////////////////////////////////////////////////////
            // KEEP THIS ENABLED
            //////////////////////////////////////////////////////

            reconnect: true,

            onSuccess: () => {
                console.log('Successfully connected MQTT client')

                setMqttClient(client)

                setMqttIsConnected(true)

                currentUser?.customer?.instruments?.forEach((instrument) => {
                    console.log(`Subscribing to /${instrument.deviceId}/status`)

                    client.subscribe(`/${instrument.deviceId}/status`)
                })
            },

            onFailure: (error) => {
                console.log('Connection failed:', error.errorMessage)

                setMqttIsConnected(false)
            },

            userName: options.userName,
            password: options.password,
            keepAliveInterval: options.keepAliveInterval
        })
    }

    ////// METHODS FOR POSTING ON TOPICS //////

    const postChangeSong = ({ message, deviceId }: { message: string; deviceId?: string }) => {
        const payLoad = {
            message: message,
            timeStamp: Date.now()
        }

        if (mqttClient?.isConnected()) {
            if (deviceId) {
                mqttClient.send(`/${deviceId}/${MQTT_API.TOPIC_ACTION.SONG}`, JSON.stringify(payLoad), 1, false)
            } else {
                customerInstruments?.forEach((instrument) => mqttClient.send(`/${instrument.deviceId}/${MQTT_API.TOPIC_ACTION.SONG}`, JSON.stringify(payLoad), 1, true))
            }
        } else {
            console.error('MQTT client not connected: unable to send change song message')
        }
    }

    const postChangeVolume = (message: string, deviceId?: string) => {
        if (mqttClient?.isConnected()) {
            mqttClient.send(`/${deviceId}/${MQTT_API.TOPIC_ACTION.VOLUME}`, message, 0, false)
        } else {
            console.error('MQTT client not connected: unable to send change volume message')
        }
    }

    const postChangeMasterVolume = (message: string) => {
        if (mqttClient?.isConnected() && connectedInstruments) {
            connectedInstruments.forEach((instrument) => {
                mqttClient.send(`/${instrument.deviceId}/${MQTT_API.TOPIC_ACTION.VOLUME}`, message, 0, false)
            })
        } else {
            console.error('MQTT client not connected: unable to send master volume message')
        }
    }

    const postResetNetwork = (deviceId: string) => {
        if (mqttClient?.isConnected()) {
            mqttClient.send(`/${deviceId}/${MQTT_API.TOPIC_ACTION.RESET_NETWORK}`, 'reset', 0, false)
        } else {
            console.error('MQTT client not connected: unable to send reset network message')
        }
    }

    const postSetInstrumentMode = ({ deviceId, touchMode }: { deviceId?: string; touchMode: TouchMode }) => {
        const payLoad = {
            touchMode: touchMode === 'EASY' ? 'simple' : 'hard'
        }

        if (mqttClient?.isConnected()) {
            if (deviceId) {
                mqttClient.send(`/${deviceId}/${MQTT_API.TOPIC_ACTION.SET_PARAMETERS}`, JSON.stringify(payLoad), 0, false)
            } else {
                Array.from(connectedInstruments, (instrument) => mqttClient.send(`/${instrument.deviceId}/${MQTT_API.TOPIC_ACTION.SET_PARAMETERS}`, JSON.stringify(payLoad), 0, false))
            }
        } else {
            console.error('MQTT client not connected: unable to send reset network message')
        }
    }

    return (
        <MqttHandlerContext.Provider
            value={{
                mqttIsConnected,
                postChangeSong,
                postChangeVolume,
                postChangeMasterVolume,
                postResetNetwork,
                postSetInstrumentMode
            }}
        >
            {children}
        </MqttHandlerContext.Provider>
    )
}

export const useMqttIsConnected = () => useContext(MqttHandlerContext).mqttIsConnected

export const usePostMqttChangeSong = () => useContext(MqttHandlerContext).postChangeSong

export const usePostMqttChangeVolume = () => useContext(MqttHandlerContext).postChangeVolume

export const usePostMqttChangeMasterVolume = () => useContext(MqttHandlerContext).postChangeMasterVolume

export const usePostMqttResetNetwork = () => useContext(MqttHandlerContext).postResetNetwork

export const usePostSetInstrumentMode = () => useContext(MqttHandlerContext).postSetInstrumentMode
